import React from 'react';
import { Button, Dialog, DialogTitle, MenuItem } from '@mui/material';
import { SimpleForm, Toolbar, useRecordContext } from 'react-admin';
import DateTimeInput from 'components/DateTimeInput';
import useDialog from 'hooks/useDialog';
import { ApplicationStatus, ApprovedStatus } from '../ApplicationStatusControl';
import { useFormContext } from 'react-hook-form';
import ScreenerAccountInput from 'components/Filter/ScreenerAccountInput';
import { getLatestActivePhoneScreening } from '../screeningUtils';

enum ApproveLabel {
  resume_review = 'Approve (Written Screen)',
  phone_screen_pending = 'Approve (Written Screen)',
}
enum ApproveConfirmationLabel {
  resume_review = 'Approve as Written Screen?',
  phone_screen_pending = 'Approve as Written Screen?',
}

interface ApproveItemProps {
  currentApplicationStatus: ApplicationStatus;
  updateApplicationStatus: (val: any) => void;
}

const ApproveItem = ({ currentApplicationStatus, updateApplicationStatus }: ApproveItemProps) => {
  const { open, closeDialog, openDialog } = useDialog();
  return (
    <>
      <MenuItem onClick={openDialog}>
        {ApproveLabel[currentApplicationStatus] ?? 'Approve'}
      </MenuItem>
      <ApproveDialog
        open={open}
        closeDialog={closeDialog}
        updateApplicationStatus={updateApplicationStatus}
        currentApplicationStatus={currentApplicationStatus}
      />
    </>
  );
};

const ApproveToolBar = ({ closeDialog, requireScheduledTime }) => {
  const { getValues } = useFormContext();
  const values = getValues();
  const isDisabled = requireScheduledTime
    ? !values?.screener_account_id || !values?.scheduled_time
    : !values?.screener_account_id;
  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button onClick={closeDialog}>Cancel</Button>
      <Button type="submit" variant="contained" color="primary" disabled={isDisabled}>
        Submit
      </Button>
    </Toolbar>
  );
};

const ApproveDialog = ({
  open,
  closeDialog,
  updateApplicationStatus,
  currentApplicationStatus,
}) => {
  const record = useRecordContext();
  const latestActivePhoneScreening = getLatestActivePhoneScreening(record);
  const isVerbalScreen = !!latestActivePhoneScreening;
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const submitApprove = ({ screener_account_id, scheduled_time }: any) => {
    updateApplicationStatus(
      isVerbalScreen
        ? {
            professional_id: record?.id,
            screener_account_id,
            scheduled_time,
            application_status: ApprovedStatus,
            reason: 'application',
            screening_type: 'phone',
          }
        : {
            professional_id: record?.id,
            screener_account_id,
            application_status: ApprovedStatus,
            reason: 'application',
            screening_type: 'written',
          },
    );
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>
        {ApproveConfirmationLabel[currentApplicationStatus] ?? 'Approve application?'}
      </DialogTitle>
      <SimpleForm
        onSubmit={submitApprove}
        defaultValues={
          isVerbalScreen
            ? {
                screener_account_id: latestActivePhoneScreening?.screener_account_id ?? undefined,
                scheduled_time: latestActivePhoneScreening?.scheduled_time ?? undefined,
              }
            : undefined
        }
        toolbar={<ApproveToolBar closeDialog={closeDialog} requireScheduledTime={isVerbalScreen} />}
      >
        <ScreenerAccountInput source="screener_account_id" label="Screening Recruiter" alwaysOn />
        {isVerbalScreen && (
          <DateTimeInput
            label="Screening Date And Time"
            source="scheduled_time"
            time_zone={browserTimezone}
            ampm
          />
        )}
      </SimpleForm>
    </Dialog>
  );
};

export default ApproveItem;
